import {Box, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useRef} from 'react';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {useIsClamped} from '../hooks/useIsClamped';
import {ExpandToggle} from './ExpandToggle';

/** Lines of the description shown before it is expanded. */
const COLLAPSED_LINE_COUNT = 6;

/** Clamps the text to a number of lines with an ellipsis; without a count it shows all of it. */
const ClampedText = styled.div<{$lineCount?: number}>`
  ${({$lineCount}) =>
    $lineCount
      ? `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${$lineCount};
    overflow: hidden;
  `
      : ''}
`;

interface DescriptionTextProps extends RequiredTestIdProps {
  description: string;
}

/** The clamped seller text with its show more toggle; measured once per description. */
export function DescriptionText(props: DescriptionTextProps) {
  const [isExpanded, , , toggleExpanded] = useBoolean();
  const clampedRef = useRef<HTMLDivElement>(null);
  const isClamped = useIsClamped(clampedRef);

  return (
    <VStack spacing={3}>
      <Box
        backgroundColor="palettes.neutral.20.100"
        borderLeft="3px solid"
        borderColor="palettes.neutral.50.100"
        borderTopRightRadius="small"
        borderBottomRightRadius="small"
        paddingHorizontal={4}
        paddingVertical={3}
      >
        <ClampedText ref={clampedRef} $lineCount={isExpanded ? undefined : COLLAPSED_LINE_COUNT}>
          <Text
            color="secondary"
            overflowWrap="anywhere"
            preWrap
            data-testid={suffixTestId('vehicleDescription', props)}
          >
            {props.description}
          </Text>
        </ClampedText>
      </Box>
      <Show when={isExpanded || isClamped}>
        <ExpandToggle
          isExpanded={isExpanded}
          expandLabel={i18n.t('entity.sourcing.vehiclePreview.actions.showMore')}
          onToggle={toggleExpanded}
          data-testid={suffixTestId('showMore', props)}
        />
      </Show>
    </VStack>
  );
}
